import { Button } from '@qr-front/ui/components/Button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@qr-front/ui/components/Dialog'
import { getVersionForClone } from '@qr-front/ui/utils'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { HelpImage } from './HelpImage'
import { stepDataListBase } from './helpStepsBase'
import { stepDataListClone1 } from './helpStepsClone1'

export type THelpStep = {
	title: string
	description: string
	imageUrlMobile: string
	imageUrlDesktop: string
}

const stepImagesList: Pick<THelpStep, 'imageUrlMobile' | 'imageUrlDesktop'>[] =
	[
		{
			imageUrlMobile: '/web-app/generator/help-popup/step-1-mobile.svg',
			imageUrlDesktop: '/web-app/generator/help-popup/step-1-desktop.svg',
		},
		{
			imageUrlMobile: '/web-app/generator/help-popup/step-2-mobile.svg',
			imageUrlDesktop: '/web-app/generator/help-popup/step-2-desktop.svg',
		},
		{
			imageUrlMobile: '/web-app/generator/help-popup/step-3-mobile.svg',
			imageUrlDesktop: '/web-app/generator/help-popup/step-3-desktop.svg',
		},
		{
			imageUrlMobile: '/web-app/generator/help-popup/step-4-mobile.svg',
			imageUrlDesktop: '/web-app/generator/help-popup/step-4-desktop.svg',
		},
		{
			imageUrlMobile: '/web-app/generator/help-popup/step-5-mobile.svg',
			imageUrlDesktop: '/web-app/generator/help-popup/step-5-desktop.svg',
		},
	]

const ProgressBar = ({
	currStep,
	stepCount,
}: {
	currStep: number
	stepCount: number
}) => {
	return (
		<div className="relative h-1 w-full bg-border-grey">
			<div
				className="absolute left-0 top-0 h-full w-[calc(100%_/_var(--step-count)_*_var(--curr-step))] rounded-r-full bg-blue transition-[width] duration-300 ease-out"
				style={{
					'--step-count': stepCount,
					'--curr-step': currStep + 1,
				}}
			/>
		</div>
	)
}

export type THelpDialogProps = {
	isOpen: boolean
	setIsOpen: (isOpen: boolean) => void
}

export const HelpDialog = ({ isOpen, setIsOpen }: THelpDialogProps) => {
	const [currStep, setCurrStep] = useState(0)

	const t = useTranslations()

	const stepDataList = getVersionForClone({
		base: stepDataListBase(t),
		clone1: stepDataListClone1(t),
	})

	const stepDataWithImagesList = stepDataList.map(
		(item, index) =>
			({
				...item,
				imageUrlMobile: stepImagesList[index]!.imageUrlMobile,
				imageUrlDesktop: stepImagesList[index]!.imageUrlDesktop,
			}) as THelpStep,
	)

	const stepCount = stepDataList.length

	const stepData = stepDataWithImagesList[currStep]!

	const isLastStep = currStep < stepCount - 1

	useEffect(() => {
		if (isOpen) {
			setCurrStep(0)
		}
	}, [isOpen])

	const buttonClose = (
		<Button visual="secondary" size="large" onClick={() => setIsOpen(false)}>
			{t('generator__help_popup__close_button')}
		</Button>
	)

	const buttonBack = (
		<Button
			visual="secondary"
			size="large"
			onClick={() => setCurrStep((prev) => prev - 1)}
		>
			{t('generator__help_popup__back_button')}
		</Button>
	)

	const buttonNext = (
		<Button size="large" onClick={() => setCurrStep((prev) => prev + 1)}>
			{t('generator__help_popup__next_button')}
		</Button>
	)

	const buttonFinish = (
		<Button size="large" onClick={() => setIsOpen(false)}>
			{t('generator__help_popup__finish_button')}
		</Button>
	)

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen}>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				className="p-0"
			>
				<HelpImage
					urlMobile={stepData.imageUrlMobile}
					urlDesktop={stepData.imageUrlDesktop}
					alt={`Step ${currStep + 1}`}
				/>
				<ProgressBar currStep={currStep} stepCount={stepCount} />
				<div className="p-6 desktop:p-8">
					<DialogHeader>
						<small className="type-body-input-title text-start text-blue">
							{t('generator__help_popup__progress', {
								step: currStep + 1,
								steps: stepCount,
							})}
						</small>
						<DialogTitle className="type-heading-4 mt-2 text-start">
							{stepData.title}
						</DialogTitle>
						<DialogDescription className="mt-1 min-h-[3lh] text-start text-body-medium desktop:min-h-[2lh]">
							{stepData.description}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="mt-6">
						{currStep === 0 ? buttonClose : buttonBack}
						{isLastStep ? buttonNext : buttonFinish}
					</DialogFooter>
				</div>
			</DialogContent>
		</Dialog>
	)
}
